import * as React from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import {
  classes,
  OutlinedButton,
  PrimaryButton,
} from '@bloomberg/media-ui-core';
import { Google } from '@bloomberg/media-ui-icons';

import { Window } from '../utils/interfaces';
import { injectScript } from '../utils/utils';

import { getClientID } from './getClientID';

const DEFAULT_GOOGLE_BUTTON_TEXT = 'Continue with Google';
const GOOGLE_CLIENT_LIBRARY_SCRIPT_SRC =
  'https://accounts.google.com/gsi/client';

export interface GoogleResponse {
  credential: string;
}

export type Props = GlobalProps & {
  onGoogleSignIn: (googleResponse: GoogleResponse) => Promise<void>;
  buttonType?: 'outlined' | 'primary';
  buttonText?: string;
  caller?: string;
  onClickCb?: () => void;
  theme?: 'light' | 'dark';
  size?: 'default' | 'small';
};

export const GoogleSignInButton = React.forwardRef<HTMLDivElement, Props>(
  function GoogleSignInButton(props: Props, ref) {
    const {
      onGoogleSignIn,
      buttonText = DEFAULT_GOOGLE_BUTTON_TEXT,
      buttonType = 'primary',
      caller,
      className,
      onClickCb,
      theme = 'light',
      size = 'small',
    } = props;

    const [loading, setLoading] = React.useState(false);

    const googleButton = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      (window as Window).onGoogleSignIn = async (googleResponse) => {
        setLoading(true);

        try {
          await onGoogleSignIn(googleResponse);
        } finally {
          setLoading(false);
        }
      };
    }, [onGoogleSignIn]);

    React.useEffect(() => {
      injectScript(GOOGLE_CLIENT_LIBRARY_SCRIPT_SRC);
    }, []);

    // This will trigger the Google Sign in UI only if the consuming app host is among the
    // "Authorized JavaScript origins" in Google OAuth2 Credential config.
    const onClick = React.useCallback((): void => {
      /* c8 ignore next */
      let button: HTMLDivElement | null | undefined =
        googleButton.current?.querySelector('div[role=button]');

      /* c8 ignore start */
      if (!button) {
        // Safari fallback where the button that Google renders may be missing the [role=button] attribute
        button = googleButton.current?.querySelector('div[tabindex="0"]');
      }
      /* c8 ignore end */

      /* c8 ignore next */
      (window as Window).newrelic?.addPageAction('googleSignIn', {
        type: 'Clicked',
        caller,
      });

      /* c8 ignore next */
      button?.click();

      if (onClickCb) {
        onClickCb();
      }
    }, []);

    return (
      <div ref={ref} className={classes([className])}>
        <div
          id="g_id_onload"
          data-client_id={getClientID()}
          data-context="signin"
          data-ux_mode="popup"
          data-callback="onGoogleSignIn"
          // Suppress Google One tap prompt
          data-auto_prompt="false"
        />
        <div
          ref={googleButton}
          style={{ display: 'none' }}
          className="g_id_signin"
        />
        {buttonType === 'primary' ? (
          <PrimaryButton
            version="phoenix"
            colorMode={theme === 'dark' ? 'dark' : 'light'}
            Icon={<Google isMultiColor={true} />}
            iconPosition="left"
            fullWidth={true}
            onClick={onClick}
            size={size}
            loading={loading}
          >
            {buttonText}
          </PrimaryButton>
        ) : (
          <OutlinedButton
            version="phoenix"
            colorMode={theme === 'dark' ? 'dark' : 'light'}
            iconPosition="left"
            Icon={<Google isMultiColor={true} />}
            fullWidth={true}
            onClick={onClick}
            size={size}
            loading={loading}
            title={'google sign-in'}
          >
            {buttonText}
          </OutlinedButton>
        )}
      </div>
    );
  }
);
